import { alpha, Theme, Components } from '@mui/material/styles';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { gray, brand } from '../settings';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import type {} from '@mui/material/themeCssVarsAugmentation';

export const InputsOverrides: Components<Theme> = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {},
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: () => ({
        fontWeight: 600,
        boxShadow: 'none',
        borderRadius: 30,
        textTransform: 'uppercase',
        color: '#FFFFFF',
        transition: 'all, 350ms ease-in-out',

        variants: [
          {
            props: {
              size: 'medium',
            },
            style: {
              padding: '12px 16px',
              fontSize: 16,
              // for some reason our webfonts lineheight is wrong
              // so we set max height manually
              height: 40,
            },
          },
          {
            props: {
              variant: 'contained',
              color: 'primary',
            },
            style: {
              '&:hover': {
                backgroundColor: '#11319A',
              },
              '&:active': {},
              '&.Mui-disabled': {
                backgroundColor: '#CEDEFDFF',
                color: '#FFFFFF',
              },
            },
          },
          {
            props: {
              variant: 'contained',
              color: 'secondary',
            },
            style: {
              backgroundColor: '#ECF1FC',
              color: '#0F60FF',
              '&:hover': {},
              '&:active': {},
              '&.Mui-disabled': {
                backgroundColor: '#EBF1FD',
                color: '#FFFFFF',
              },
            },
          },

          {
            props: {
              variant: 'text',
            },
            style: {
              color: '#0F60FF',
              fontSize: 16,
              fontWeight: 400,
              // color: gray[600],
              '&:hover': {
                backgroundColor: '#ECF1FC',
              },
              '&:active': {
                // backgroundColor: gray[200],
              },
            },
          },
        ],
      }),
    },
  },

  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        // borderRadius: "10px",
        // boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          // color: brand[500],
        },
        ...theme.applyStyles('dark', {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            // color: "#fff",
          },
          // boxShadow: `0 4px 16px ${alpha(brand[700], 0.5)}`,
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        // padding: "12px 16px",
        // textTransform: "none",
        // borderRadius: "10px",
        // fontWeight: 500,
        ...theme.applyStyles('dark', {
          // color: gray[400],
          // boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
          [`&.${toggleButtonClasses.selected}`]: {
            // color: brand[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon
          sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }}
        />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: '1px solid ',
        borderColor: alpha(gray[300], 0.8),
        boxShadow: '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
        backgroundColor: alpha(gray[100], 0.4),
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': {
          borderColor: brand[300],
        },
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: '2px',
          borderColor: brand[400],
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          '&:hover': {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: '0 0 0 1.5px hsl(210, 0%, 0%) inset',
          backgroundColor: alpha(gray[900], 0.8),
          '&:hover': {
            borderColor: brand[300],
          },
          '&.Mui-focusVisible': {
            borderColor: brand[400],
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    defaultProps: {},
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
      input: {},
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
    },
    styleOverrides: {
      root: {},
    },
  },
  MuiInputLabel: {
    defaultProps: {},
    styleOverrides: {
      root: {
        fontSize: 19,
        transform: 'translate(24px, 14px) scale(1)',
        color: 'inherit',
        '&.Mui-focused': {
          color: 'inherit',
        },
        '&.Mui-focused, &.MuiFormLabel-filled ': {
          transform: 'translate(23px, 7px) scale(0.55)',
        },
      },
    },
  },
  MuiFilledInput: {
    defaultProps: {
      disableUnderline: true,
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        paddingRight: '8px',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        transition: theme.transitions.create([
          'background-color',
          'box-shadow',
        ]),
        fontSize: 19,
        lineHeight: '24px',
        color: 'inherit',
        input: {
          color: '#0032A0',
          transition: theme.transitions.create([
            'background-color',
            'box-shadow',
          ]),
          height: 48,
          paddingTop: 8,
          paddingRight: 8,
          paddingBottom: 0,
          paddingLeft: 24,
          fontSize: 19,
          '&:autofill': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 0,
          },
        },

        '&.Mui-focused': {},
      }),
      // disabled: {},
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: ExpandMoreRoundedIcon,
      MenuProps: {
        elevation: 3,
        sx: {
          marginTop: '4px',
        },
      },
    },
    styleOverrides: {
      icon: {},
      filled: {},
      multiple: {},
    },
  },
};
